import { IconFont, Ternary } from '@/components';
import { cn } from '@/utils';
import { Form, FormListOperation, Input, Radio, Select, Typography, RadioChangeEvent } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import useExtractTags from '../../../components/extract-tag-select/use-extract-tags';
import { useStore } from '../../../store';
import { isNil } from 'lodash';
import { Field } from '../../semantic-rules-extraction/form/type';

const { Option } = Select;

export const FieldItem: FC<{
  prefix: (string | number)[];
  fullPrefix: (string | number)[];
  operation: FormListOperation;
  readonly?: boolean;
  node_id: string;
  field: Field;
  activeField: number;
  setActiveField: (key: number) => void;
}> = ({
  prefix,
  fullPrefix,
  operation,
  readonly,
  node_id,
  field,
  activeField,
  setActiveField,
}) => {
  const form = Form.useFormInstance();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { extractTags } = useExtractTags();
  const tagCode: string = Form.useWatch([...fullPrefix, 'tag_code']);
  const type: string = Form.useWatch([...fullPrefix, 'type']);
  const tableCode: string = Form.useWatch([...fullPrefix, 'table_code']);
  const formTableTagList = Form.useWatch([...fullPrefix, 'table_tag_list']);

  const emitter = useStore((s) => s.eventEmitter);

  const tableTagList = extractTags?.table_tags?.find(item => item.table_code === tableCode)?.tags;

  useEffect(() => {
    if (type === 'TABLE') {
      const formTagMap = new Map(
        (formTableTagList || []).map((item: any) => [item.tag_code, item])
      );

      const mergedTagList = (tableTagList || []).map(item => {
        const existing = formTagMap.get(item.tag_code);
        return existing ?? { tag_code: item.tag_code, prompt: '' };
      });

      form.setFieldValue([...fullPrefix, 'table_tag_list'], mergedTagList);
    }
  }, [tableTagList]);

  const tag = useMemo(
    () => {
      if (type === 'TEXT') {
        return extractTags?.text_tags?.find(item => item.tag_code === tagCode)?.tag_name
      } else {
        return extractTags?.table_tags?.find(item => item.table_code === tableCode)?.table_name
      }
    },
    [extractTags, type, tagCode, tableCode],
  );

  useEffect(() => {
    const currentValue = form.getFieldValue([...fullPrefix]);
    if (!isNil(currentValue?.tag_code) || !isNil(currentValue?.table_code)) return;
    setIsCollapsed(false);
  }, []);

  const onChangeTagCode = () => {
    // 清空任务提示词
    form.setFieldValue([...fullPrefix, 'prompt'], undefined);
  };

  const onChangeTableCode = () => {
    // 清空表格级提示词
    form.setFieldValue([...fullPrefix, 'table_prompt'], undefined);

    // 清空每个字段的 prompt
    const currentTableTagList = form.getFieldValue([...fullPrefix, 'table_tag_list']) || [];
    const cleared = currentTableTagList.map((item: any) => ({
      ...item,
      prompt: '', // 或 undefined，看校验规则是否允许
    }));
    form.setFieldValue([...fullPrefix, 'table_tag_list'], cleared);
  };

  const onTypeChange = (e: RadioChangeEvent) => {
    const newType = e.target.value;
    form.setFieldValue([...fullPrefix, 'type'], newType);

    if (newType === 'TEXT') {
      form.setFieldValue([...fullPrefix, 'table_code'], undefined);
      form.setFieldValue([...fullPrefix, 'table_prompt'], undefined);
      form.setFieldValue([...fullPrefix, 'table_tag_list'], undefined);
    } else {
      form.setFieldValue([...fullPrefix, 'tag_code'], undefined);
      form.setFieldValue([...fullPrefix, 'prompt'], undefined);
    }
  };

  return (
    <div
      key={field.key}
      onClick={() => setActiveField(field.key)}
      className={`relative mb-4 p-2 rounded-md transition  hover:shadow  border border-[#E5E7EC]
    ${activeField === field.key ? 'bg-[#f6f9ff]' : 'bg-white'}
    `}
    >
      <div className="-top-2 right-2 absolute flex items-center gap-2">
        <IconFont
          onClick={() => setIsCollapsed(!isCollapsed)}
          type={isCollapsed ? 'icon-zhankaikapian' : 'icon-shouqikapian'}
          style={{
            cursor: 'pointer',
            transform: isCollapsed ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s',
          }}
        />
        <IconFont
          className={cn({
            'cursor-not-allowed pointer-events-none': readonly,
          })}
          // onClick={() => onDelete?.(field.name)}
          onClick={() => {
            if (readonly) return;
            operation.remove(Number(prefix[0]));
          }}
          type="icon-liebiaoshibai"
        />
      </div>

      <div>
        <div style={{ display: isCollapsed ? 'none' : 'block' }}>
          <Form.Item
            label="抽取类型"
            name={[...prefix, 'type']}
            initialValue="TEXT"
          >
            <Radio.Group onChange={onTypeChange}>
              <Radio value="TEXT">文本字段</Radio>
              <Radio value="TABLE">表格字段</Radio>
            </Radio.Group>
          </Form.Item>
          {type === 'TEXT' && <Form.Item label="字段" name={[...prefix, 'tag_code']}>
            <Select placeholder="请选择字段" onChange={onChangeTagCode}>
              {extractTags?.text_tags?.map(item => <Option value={item.tag_code}>{item.tag_name}</Option>)}
            </Select>
          </Form.Item>}
          {type === 'TABLE' && <Form.Item label="表格名称" name={[...prefix, 'table_code']}>
            <Select placeholder="请选择表格名称" onChange={onChangeTableCode}>
              {extractTags?.table_tags?.map(item => <Option value={item.table_code}>{item.table_name}</Option>)}
            </Select>
          </Form.Item>}
          {type === 'TEXT' && <Form.Item
            label="任务提示词"
            name={[...prefix, 'prompt']}
            rules={[{ required: true, message: '请输入任务提示词' }]}
            tooltip="对字段进行解释说明或提供一些示例，便于大模型更好地理解你所需要抽取的字段"
          >
            <Input.TextArea placeholder="请输入任务提示词" rows={3} maxLength={5500} />
          </Form.Item>}
          {type === 'TABLE' && <Form.Item
            label="表格提示词"
            name={[...prefix, 'table_prompt']}
            rules={[{ required: true, message: '请输入表格提示词' }]}
          >
            <Input.TextArea placeholder="请输入表格提示词" rows={3} maxLength={5500} />
          </Form.Item>}
          <Form.Item
            label="抽取来源"
            name={[...prefix, 'source']}
            initialValue="ALL"
            tooltip="选择抽取来源，默认从文档全部内容中抽取，即包括文本和表格。也可选择从文档中的纯文本内容抽取，或从文档中的表格抽取。"
          >
            <Radio.Group>
              <Radio value="ALL">从全文抽取</Radio>
              <Radio value="TEXT">仅从文本抽取</Radio>
              <Radio value="TABLE">仅从表格抽取</Radio>
            </Radio.Group>
          </Form.Item>
          {type === 'TABLE' && (
            <Form.List name={[...prefix, 'table_tag_list']}>
              {(fields) => (
                <>
                  {fields.map((field, index) => (
                    <div key={field.key}>
                      <Typography.Text>抽取字段：{tableTagList?.[index]?.tag_name}</Typography.Text>
                      <Form.Item
                        {...field}
                        label="抽取字段"
                        name={[field.name, 'tag_code']}
                        initialValue={tableTagList?.[index]?.tag_code}
                        noStyle
                      >
                        <Input type="hidden" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        label="字段提示词"
                        name={[field.name, 'prompt']}
                      >
                        <Input.TextArea placeholder="请输入字段提示词" rows={3} maxLength={5500} />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          )}
        </div>
        <div
          style={{ display: isCollapsed ? 'flex' : 'none' }}
          className="justify-between items-center gap-2"
        >
          <Ternary
            condition={Boolean(tag)}
            falseElement={<span className="text-slate-400">未选择字段</span>}
          >
            <span className="font-bold">{tag}</span>
          </Ternary>
        </div>
      </div>
    </div>
  );
};
